/**
 * AbilitySystem.ts — Class ability evolution tracking.
 *
 * Tracks current tier per ability per class. Checks evolution triggers
 * (story flags and chapter progression) and emits events on tier change.
 *
 * Ability tiers are stored in GameState.abilityTiers as "abilityId" → tier number.
 * Tier 1 is the starting tier (available at character creation).
 */

import type { ClassId } from '@game/data/types';
import type { AbilityDefinition, AbilityTier } from '@game/data/types';
import { getClassAbilities } from '@game/data/abilities';
import type { GameState } from '@core/engine/GameState';
import type { EventSystem } from '@core/engine/EventSystem';

/** Info about a single ability at its current evolution tier. */
export interface AbilityInfo {
  id: string;
  name: string;
  type: string;
  description: string;
  currentTier: number;
  maxTier: number;
  tierData: AbilityTier;
  canEvolve: boolean;
  nextTier?: AbilityTier;
}

/** Result of an evolution check — which abilities evolved. */
export interface EvolutionResult {
  abilityId: string;
  abilityName: string;
  fromTier: number;
  toTier: number;
  tierName: string;
  evolutionNarrative?: string;
  storyFlag?: string;
}

export class AbilitySystem {
  private events: EventSystem;

  constructor(events: EventSystem) {
    this.events = events;
  }

  /**
   * Initialize ability tiers for a new character.
   * Sets all abilities for the class to tier 1.
   */
  initializeAbilities(classId: ClassId, state: GameState): void {
    const classAbilities = getClassAbilities(classId);
    if (!classAbilities) return;

    for (const ability of classAbilities.abilities) {
      state.abilityTiers.set(ability.id, 1);
    }
  }

  /** Get the current tier number for an ability (defaults to 1). */
  getCurrentTier(abilityId: string, state: GameState): number {
    return state.abilityTiers.get(abilityId) ?? 1;
  }

  /**
   * Get full info for all abilities of a class at their current evolution state.
   */
  getAbilitiesForClass(classId: ClassId, state: GameState): AbilityInfo[] {
    const classAbilities = getClassAbilities(classId);
    if (!classAbilities) return [];

    return classAbilities.abilities.map(ability =>
      this.getAbilityInfo(ability, state),
    );
  }

  /**
   * Get info for a single ability at its current tier.
   */
  getAbilityInfo(ability: AbilityDefinition, state: GameState): AbilityInfo {
    const currentTier = this.getCurrentTier(ability.id, state);
    const maxTier = ability.tiers.length;
    const tierData = ability.tiers[currentTier - 1] ?? ability.tiers[0];
    const nextTierData = currentTier < maxTier
      ? ability.tiers[currentTier]
      : undefined;

    const canEvolve = nextTierData
      ? this.canEvolveToTier(nextTierData, state)
      : false;

    return {
      id: ability.id,
      name: ability.name,
      type: ability.type,
      description: ability.description,
      currentTier,
      maxTier,
      tierData,
      canEvolve,
      nextTier: nextTierData,
    };
  }

  /**
   * Check if a tier's unlock conditions are met.
   * Checks both the chapter requirement and the story flag trigger.
   */
  canEvolveToTier(tier: AbilityTier, state: GameState): boolean {
    // Check chapter requirement
    if (state.story.currentChapter < tier.unlockChapter) {
      return false;
    }

    // Check story flag trigger (e.g., "act-1-complete", "frostcrown-assembled")
    if (tier.unlockTrigger && tier.unlockTrigger !== 'character-creation') {
      if (!state.getFlag(tier.unlockTrigger)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check all abilities for the current class and evolve any that qualify.
   * Returns a list of evolutions that occurred.
   */
  checkEvolutions(classId: ClassId, state: GameState): EvolutionResult[] {
    const classAbilities = getClassAbilities(classId);
    if (!classAbilities) return [];

    const results: EvolutionResult[] = [];

    for (const ability of classAbilities.abilities) {
      const currentTier = this.getCurrentTier(ability.id, state);

      // Check each tier above the current one (in case multiple tiers can be unlocked at once)
      for (let t = currentTier + 1; t <= ability.tiers.length; t++) {
        const tierData = ability.tiers[t - 1];
        if (!this.canEvolveToTier(tierData, state)) break;

        // Evolve to this tier
        state.abilityTiers.set(ability.id, t);

        // Set the story flag if defined
        if (tierData.storyFlag) {
          state.setFlag(tierData.storyFlag, true);
        }

        const result: EvolutionResult = {
          abilityId: ability.id,
          abilityName: ability.name,
          fromTier: t - 1,
          toTier: t,
          tierName: tierData.name,
          evolutionNarrative: tierData.evolutionNarrative,
          storyFlag: tierData.storyFlag,
        };

        results.push(result);

        this.events.emit('abilityEvolved', {
          abilityId: ability.id,
          abilityName: ability.name,
          tierName: tierData.name,
          fromTier: t - 1,
          toTier: t,
        });
      }
    }

    return results;
  }

  /**
   * Manually evolve an ability to a specific tier (for testing/debug).
   * Returns the evolution result, or null if the ability or tier is invalid.
   */
  evolveAbility(
    abilityId: string,
    tier: number,
    state: GameState,
  ): EvolutionResult | null {
    const currentTier = this.getCurrentTier(abilityId, state);
    if (tier <= currentTier) return null;

    // Find the ability in any class to get tier data
    const classAbilities = getClassAbilities(state.character.classId);
    if (!classAbilities) return null;

    const ability = classAbilities.abilities.find(a => a.id === abilityId);
    if (!ability) return null;

    const tierData = ability.tiers[tier - 1];
    if (!tierData) return null;

    state.abilityTiers.set(abilityId, tier);

    if (tierData.storyFlag) {
      state.setFlag(tierData.storyFlag, true);
    }

    const result: EvolutionResult = {
      abilityId: ability.id,
      abilityName: ability.name,
      fromTier: currentTier,
      toTier: tier,
      tierName: tierData.name,
      evolutionNarrative: tierData.evolutionNarrative,
      storyFlag: tierData.storyFlag,
    };

    this.events.emit('abilityEvolved', {
      abilityId: ability.id,
      abilityName: ability.name,
      tierName: tierData.name,
      fromTier: currentTier,
      toTier: tier,
    });

    return result;
  }
}
